import { makeStyles } from "tss-react/mui";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { Box } from "@mui/material";
import { DataGridPremium, gridClasses } from "@mui/x-data-grid-premium";

const generateColumns = (fields, classes) =>
  fields.map(({ label, headerName, field = "", renderCell, ...props }) => {
    const columnLabel = label ?? headerName ?? field;
    const customRenderCell = !field && !renderCell ? () => null : renderCell;

    return {
      field,
      headerName: columnLabel,
      renderCell: customRenderCell,
      valueGetter: customRenderCell
        ? undefined
        : (_value, row) =>
            parseFieldValue(
              getNestedValue(row, field),
              columnLabel.toLowerCase().includes("date")
                ? "--/--/--"
                : undefined,
              undefined,
              { isCurrency: field === "variance", currency: "USD" }
            ),
      headerClassName: classes.tableHeaderCell,
      cellClassName: classes.tableCell,
      ...props,
    };
  });

export const MiPrintTableContent = ({
  data = [],
  columns = [],
  columnHeaderHeight = 24,
  rowHeight = 58,
  gridProps,
}) => {
  const { classes } = useStyles({ cellMinHeight: rowHeight });
  const isAutoHeight = gridProps?.autoHeight;

  const rows = data.map((item, index) => ({
    id: item?.id || index,
    ...item,
  }));

  return (
    <Box className={classes.tableContainer}>
      <DataGridPremium
        hideFooter
        rows={rows}
        className={`${classes.dataGrid} ${
          isAutoHeight ? classes.autoHeightDataGrid : ""
        }`}
        columns={generateColumns(columns, classes)}
        columnHeaderHeight={columnHeaderHeight}
        {...gridProps}
        hideFooterPagination
        pagination={false}
        disableVirtualization
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme, { cellMinHeight }) => ({
  dataGrid: {
    borderRadius: 0,
    width: "100%",
    "*": {
      overflow: "hidden !important",
    },
  },
  autoHeightDataGrid: {
    flex: "0 0 auto",
    height: "auto",
    minHeight: 0,
    "@media print": {
      overflow: "visible !important",
      [`& .${gridClasses.main}`]: {
        flexGrow: 0,
        height: "auto !important",
        overflow: "visible !important",
      },
      [`& .${gridClasses["container--top"]}`]: {
        position: "static !important",
      },
      [`& .${gridClasses.virtualScroller}`]: {
        height: "auto !important",
        flexGrow: 0,
        overflow: "visible !important",
      },
      [`& .${gridClasses.virtualScrollerContent}`]: {
        height: "auto !important",
        minHeight: "0 !important",
      },
      [`& .${gridClasses.virtualScrollerRenderZone}`]: {
        position: "static !important",
        transform: "none !important",
      },
      [`& .${gridClasses.row}`]: {
        position: "static !important",
        transform: "none !important",
      },
    },
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    [`.${gridClasses.columnHeader}`]: {
      backgroundColor: theme.palette.grey.lightAccent,
    },
    [`.${gridClasses.cell}.grey`]: {
      backgroundColor: theme.palette.grey["100"],
    },
    [`.${gridClasses.cell}.blue`]: {
      backgroundColor: theme.palette.blue.light,
      color: theme.palette.blue.dark,
    },
    [`.${gridClasses.cell}.bold`]: {
      fontWeight: theme.fontWeight.black,
    },
  },
  tableHeaderCell: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.fontWeight.medium,
  },
  tableCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: theme.typography.pxToRem(13.5),
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "unset",
    wordBreak: "break-word",
    minHeight: cellMinHeight,
    borderRight: `1px solid ${theme.palette.grey.accent}`,
  },
}));
